import React, { useCallback, useRef, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const CHANNELS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

export default function MessageInput({
  onSend,
  onSendNote,
  defaultChannel = "whatsapp",
  disabled = false,
  templates = [],
  composerValue,
  onComposerChange,
}) {
  const [channel, setChannel] = useState(defaultChannel);
  const [showTemplates, setShowTemplates] = useState(false);
  const inputRef = useRef(null);

  const handleSend = useCallback(() => {
    if (!composerValue.trim()) return;
    onSend({ content: composerValue.trim(), channel });
  }, [composerValue, channel, onSend]);

  const handleNote = useCallback(() => {
    if (!composerValue.trim()) return;
    onSendNote(composerValue.trim());
  }, [composerValue, onSendNote]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const insertTemplate = (tpl) => {
    onComposerChange(tpl.content);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  return (
    <div
      style={{
        borderTop: "1px solid #e2e8f0",
        padding: "8px 12px",
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          style={{
            fontSize: 12,
            padding: "3px 6px",
            borderRadius: 6,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          {CHANNELS.map((ch) => (
            <option key={ch.value} value={ch.value}>
              {ch.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowTemplates(!showTemplates)}
          title="Templates"
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 4,
            borderRadius: 4,
            color: "#64748b",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {showTemplates && templates.length > 0 && (
        <div
          style={{
            maxHeight: 160,
            overflowY: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            marginBottom: 6,
            background: "#f8fafc",
          }}
        >
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => insertTemplate(tpl)}
              style={{
                padding: "6px 10px",
                cursor: "pointer",
                fontSize: 13,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <strong>{tpl.name}</strong>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {tpl.content.slice(0, 80)}...
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Type a message..."
          value={composerValue}
          onChange={(e) => onComposerChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{
            flex: 1,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 14,
            outline: "none",
          }}
          aria-label="Message"
        />

        <button
          type="button"
          onClick={handleNote}
          disabled={disabled || !composerValue.trim()}
          title="Add internal note"
          style={{
            border: "none",
            background: "#fef3c7",
            borderRadius: 8,
            padding: "7px 10px",
            cursor: "pointer",
            color: "#92400e",
          }}
        >
          <NoteAddOutlinedIcon sx={{ fontSize: 20 }} />
        </button>

        <button
          type="button"
          onClick={handleSend}
          disabled={disabled || !composerValue.trim()}
          aria-label="Send"
          style={{
            border: "none",
            background: "#7c3aed",
            borderRadius: 8,
            padding: "7px 12px",
            cursor: "pointer",
            color: "#fff",
            opacity: disabled || !composerValue.trim() ? 0.5 : 1,
          }}
        >
          <SendIcon sx={{ fontSize: 20 }} />
        </button>
      </div>
    </div>
  );
}
